import { Badge } from "@volca/ui/components/ui/badge";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { Field } from "./statement-field";
import { GRID } from "./statement-layout";
import type { SummaryBlock } from "./statement-schema";

const PaidBadge = () => (
  <Badge
    variant="secondary"
    color="success"
    size="sm"
    className="tracking-widest uppercase"
  >
    Paid
  </Badge>
);

export const Summary = ({ block }: { block: SummaryBlock }) => {
  const paid = block.variant === "paid";
  const unpaid = block.variant === "unpaid";
  const isTile = paid || unpaid;

  const hero = block.hero && (
    <Stack align="start" gap={0} className="min-w-0 gap-1">
      <p
        className={cn(
          "text-xs font-medium tracking-widest text-brand uppercase",
          {
            "text-success": paid,
            "text-subtle": unpaid,
          },
        )}
      >
        {block.hero.label}
      </p>
      <Text
        variant="display"
        className={cn("text-brand", {
          "text-success": paid,
          "text-subtle": unpaid,
        })}
      >
        {block.hero.value}
      </Text>
    </Stack>
  );

  const body = (
    <Stack align="start" gap={0} className="w-full gap-4">
      {/* Plain cards render the hero unwrapped (DOM-identical to before). Tiles
          put the hero + Paid badge in a top row; the badge is independent of the
          optional hero, so a metrics-only paid tile still shows it. */}
      {isTile
        ? (block.hero || paid) && (
            <Stack
              className="w-full"
              direction="row"
              align="start"
              justify={block.hero ? "between" : "end"}
              gap={4}
            >
              {hero}
              {paid && <PaidBadge />}
            </Stack>
          )
        : hero}
      {block.metrics && block.metrics.length > 0 && (
        <div className={GRID}>
          {block.metrics.map((metric, index) => (
            <Field key={index} metric={metric} prominent muted={unpaid} />
          ))}
        </div>
      )}
    </Stack>
  );

  if (!isTile) return body;

  return (
    <Stack
      align="start"
      gap={0}
      className={cn("w-full", "rounded-xl border p-4", {
        "border-success-line bg-success-subtle": paid,
        "border-line bg-neutral/50": unpaid,
      })}
    >
      {body}
    </Stack>
  );
};
